import { useCallback, useEffect, useRef, useState } from "react";

import type {
  ApiClient,
  RoutingProfileView,
  RoutingTerm,
  RoutingTermChange,
} from "../../shared/api/client";
import { useLocale } from "../../shared/i18n";
import type { MessageKey } from "../../shared/i18n";
import { Badge, Button, Input, Sheet } from "../../shared/ui";

type KeywordsApi = Pick<ApiClient, "routingProfile" | "editRoutingTerms">;

/** Value of the "add to" select that means a typical request, not a target. */
const PHRASE = "";
const SOURCE: Record<RoutingTerm["source"], MessageKey> = {
  labels: "keywords.source.labels",
  suggested: "keywords.source.suggested",
  owner: "keywords.source.owner",
};
const REFUSALS: Record<string, MessageKey> = {
  routing_term_invalid: "keywords.invalid",
  routing_terms_full: "keywords.full",
};
const CONFLICTS = new Set([
  "interface_draft_stale",
  "interface_stale_draft",
  "interface_base_changed",
  "interface_draft_conflict",
]);

function codeOf(cause: unknown): string | undefined {
  const detail =
    cause instanceof Error
      ? (cause.cause as { code?: unknown } | undefined)
      : undefined;
  return typeof detail?.code === "string" ? detail.code : undefined;
}

/**
 * The words the assistant uses to tell which section a request is about
 * (ADR 0236). The owner can switch a word off or bring it back, add a word of
 * their own to a table, a field or a screen, and add a typical request. Every
 * change is an `edit_interface` operation limited to the owner's lists: it
 * lands in the section's Draft, reviewed and published like any other edit.
 */
export function KeywordsSheet({
  open,
  onOpenChange,
  api,
  abilityId,
  sectionName,
  onDraftReady,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: KeywordsApi;
  abilityId: string;
  sectionName: string;
  /** Go and review the Draft that holds the changes. */
  onDraftReady: () => void;
}) {
  const { locale, t } = useLocale();
  const [profile, setProfile] = useState<RoutingProfileView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [target, setTarget] = useState<string>("ability");
  const [word, setWord] = useState("");

  // The caller may build `api` on every render; the profile is fetched per opening.
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  });
  const load = useCallback(() => {
    return apiRef.current
      .routingProfile(abilityId)
      .then(setProfile)
      .catch(() => {
        setLoadFailed(true);
      });
  }, [abilityId]);
  useEffect(() => {
    if (!open) return;
    void load();
    return () => {
      setProfile(null);
      setLoadFailed(false);
      setMessage(null);
      setSaved(false);
      setWord("");
    };
  }, [load, open]);

  const apply = (change: RoutingTermChange) => {
    setBusy(true);
    setMessage(null);
    api
      .editRoutingTerms(abilityId, [change], locale === "ru" ? "ru" : "en")
      .then(async (result) => {
        const refused = result.refused[0];
        if (refused) {
          setMessage(REFUSALS[refused.code] ?? "keywords.failed");
          return;
        }
        if (result.saved) {
          setSaved(true);
          setWord("");
          await load();
        }
      })
      .catch((cause: unknown) => {
        const code = codeOf(cause) ?? "";
        setMessage(
          CONFLICTS.has(code) ? "keywords.conflict" : "keywords.failed",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const submit = () => {
    const term = word.trim();
    if (term === "") return;
    apply({
      op: "add_routing_term",
      ...(target === PHRASE ? {} : { target }),
      term,
    });
  };

  const termRow = (item: RoutingTerm, key: string | undefined) => {
    const change = (op: RoutingTermChange["op"]): RoutingTermChange => ({
      op,
      ...(key === undefined ? {} : { target: key }),
      term: item.term,
    });
    const owned = item.source === "owner";
    return (
      <li
        key={`${item.source}:${item.term}`}
        className="flex min-h-11 items-center gap-2 py-1"
      >
        <span
          className={`min-w-0 flex-1 text-body ${
            item.removed ? "text-fg-subtle line-through" : ""
          }`}
        >
          {item.term}
        </span>
        <Badge tone={owned ? "success" : "neutral"}>
          {item.removed ? t("keywords.off") : t(SOURCE[item.source])}
        </Badge>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          aria-label={`${t(
            item.removed
              ? "keywords.switchOn"
              : owned
                ? "keywords.remove"
                : "keywords.switchOff",
          )}: ${item.term}`}
          onClick={() => {
            apply(
              change(item.removed ? "add_routing_term" : "remove_routing_term"),
            );
          }}
        >
          {item.removed ? "+" : "×"}
        </Button>
      </li>
    );
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={`${t("keywords.title")} «${sectionName}»`}
      description={t("keywords.hint")}
      footer={
        saved ? (
          <Button onClick={onDraftReady}>{t("keywords.review")}</Button>
        ) : undefined
      }
    >
      {saved && (
        <p role="status" className="mb-3 text-body text-success">
          {t("keywords.saved")}
        </p>
      )}
      {message && (
        <p role="alert" className="mb-3 text-body text-warning">
          {t(message)}
        </p>
      )}
      {loadFailed ? (
        <p className="text-body text-error">{t("keywords.loadFailed")}</p>
      ) : profile === null ? (
        <p className="text-body text-fg-muted" aria-busy="true">
          {t("startup.loadingRoute")}
        </p>
      ) : (
        <>
          {profile.draft_pending && (
            <p className="mb-3 text-caption text-fg-muted">
              {t("keywords.draftShown")}
            </p>
          )}
          <form
            className="mb-4 flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <label className="text-footnote font-medium text-fg-muted">
              {t("keywords.addTo")}
              <select
                className="mt-1 h-12 w-full rounded-lg bg-sunken px-3.5 text-body text-fg"
                value={target}
                onChange={(event) => {
                  setTarget(event.target.value);
                }}
              >
                {profile.targets.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
                <option value={PHRASE}>{t("keywords.addPhrase")}</option>
              </select>
            </label>
            <label className="text-footnote font-medium text-fg-muted">
              {target === PHRASE ? t("keywords.addPhrase") : t("keywords.word")}
              <Input
                className="mt-1"
                value={word}
                maxLength={target === PHRASE ? 120 : 60}
                onChange={(event) => {
                  setWord(event.target.value);
                }}
              />
            </label>
            <Button type="submit" disabled={busy || word.trim() === ""}>
              {t("keywords.add")}
            </Button>
          </form>
          {profile.targets.map((item) => (
            <section key={item.key} className="mb-4">
              <h3 className="text-caption font-bold uppercase tracking-wide text-fg-muted">
                {item.label}
              </h3>
              <ul>{item.terms.map((entry) => termRow(entry, item.key))}</ul>
            </section>
          ))}
          {profile.phrases.length > 0 && (
            <section className="mb-4">
              <h3 className="text-caption font-bold uppercase tracking-wide text-fg-muted">
                {t("keywords.phrases")}
              </h3>
              <ul>
                {profile.phrases.map((entry) => termRow(entry, undefined))}
              </ul>
            </section>
          )}
        </>
      )}
    </Sheet>
  );
}
